import type {ReactElement} from 'react';
import React from 'react';
import type {PublishingOptionsProps} from './publishing-options.types';
import {PublishingOption} from './publishing-options.types';
import {ControlledListItemType7} from '@Components/controlled-list/components/controlled-list-item-type-7';
import {LoadingStates} from '@Utils/loading.util';
import {ChatImageActionPanelId} from '@Components/ai-chat-window/components/generated-image-actions-icons/generated-image-actions-icons.types';
import {closeModal} from '@Components/modal-container/modal-container-reducer';
import {isUserPAYG} from '@Libraries/user.library';
import styles from './publishing-options.module.scss';

export function PublishingOptions({removeLastItemBottomMargin = true, excludedOptions = [], ...props}: PublishingOptionsProps): ReactElement {
  const getPublishingOptions = (): ReactElement => {
    const isDisabled = props.loadingStates.publishingLoadingState === LoadingStates.LOADING;
    const isUserPayg = isUserPAYG();
    const visibleOptions = [PublishingOption.PUBLISH_TO_SOCIAL_MEDIA, PublishingOption.PROMOTE_AS_EVENT, PublishingOption.PUBLISH_AS_EMAIL_CAMPAIGN].filter(
      (option) => !excludedOptions.includes(option)
    );

    const isLastVisibleOption = (option: PublishingOption): boolean => {
      return visibleOptions[visibleOptions.length - 1] === option;
    };

    const getItemClassName = (option: PublishingOption): string => {
      return `radius-8 ${removeLastItemBottomMargin && isLastVisibleOption(option) ? '_unmargin' : ''}`;
    };

    return (
      <>
        {!excludedOptions.includes(PublishingOption.PUBLISH_TO_SOCIAL_MEDIA) ? (
          <ControlledListItemType7
            id={PublishingOption.PUBLISH_TO_SOCIAL_MEDIA}
            text={window.i18next.t('pmwjs_post_on_social_media')}
            icon={'icon-social-media'}
            onClick={() => {
              closeModal(ChatImageActionPanelId.MORE_OPTIONS_DROPDOWN_MODAL_PANEL_ID);
              void props.publishToSocialMedia();
            }}
            className={getItemClassName(PublishingOption.PUBLISH_TO_SOCIAL_MEDIA)}
            isLoading={
              props.loadingStates.loadingPublishingOption === PublishingOption.PUBLISH_TO_SOCIAL_MEDIA && props.loadingStates.publishingLoadingState === LoadingStates.LOADING
            }
            isDisabled={isDisabled}
          />
        ) : null}
        {!excludedOptions.includes(PublishingOption.PROMOTE_AS_EVENT) ? (
          <ControlledListItemType7
            id={PublishingOption.PROMOTE_AS_EVENT}
            text={window.i18next.t('pmwjs_turn_into_event_page')}
            icon={'icon-event'}
            onClick={() => {
              closeModal(ChatImageActionPanelId.MORE_OPTIONS_DROPDOWN_MODAL_PANEL_ID);
              void props.promoteAsEvent();
            }}
            className={getItemClassName(PublishingOption.PROMOTE_AS_EVENT)}
            isLoading={props.loadingStates.loadingPublishingOption === PublishingOption.PROMOTE_AS_EVENT && props.loadingStates.publishingLoadingState === LoadingStates.LOADING}
            isDisabled={isDisabled}
          />
        ) : null}
        {!excludedOptions.includes(PublishingOption.PUBLISH_AS_EMAIL_CAMPAIGN) ? (
          <ControlledListItemType7
            id={PublishingOption.PUBLISH_AS_EMAIL_CAMPAIGN}
            text={window.i18next.t('pmwjs_send_as_email')}
            icon={isUserPayg ? 'icon-crown' : 'icon-envelope'}
            onClick={() => {
              closeModal(ChatImageActionPanelId.MORE_OPTIONS_DROPDOWN_MODAL_PANEL_ID);
              void props.publishAsEmailCampaign();
            }}
            iconClasses={isUserPayg ? styles.crownIcon : ''}
            className={getItemClassName(PublishingOption.PUBLISH_AS_EMAIL_CAMPAIGN)}
            isLoading={
              props.loadingStates.loadingPublishingOption === PublishingOption.PUBLISH_AS_EMAIL_CAMPAIGN && props.loadingStates.publishingLoadingState === LoadingStates.LOADING
            }
            isDisabled={isDisabled}
          />
        ) : null}
      </>
    );
  };

  return getPublishingOptions();
}
